/**
 * Toggle — binary on/off switch with immediate-effect semantics.
 *
 * Unlike Checkbox/Radio (which are form controls — pick, then submit),
 * Toggle fires an action immediately. It pairs naturally with async
 * back-end calls — pass an `onChange` that returns a `Promise<void>`
 * and the thumb is replaced by a spinner if the call hasn't settled
 * within 500 ms.
 *
 * Geometry:
 *   - row matches Radio / Checkbox at the same step: s=24 / m=32 / l=48.
 *   - track ratio ≈ 1.625 : 1 (modern, not the cartoonish 2 : 1).
 *     s = 39 × 24, m = 52 × 32, l = 78 × 48.
 *   - thumb = trackHeight − 2*pad. Padding 4 on every step.
 *
 * Visual states:
 *   - default: track in color.main (on) or surface3 (off).
 *   - hover:   thumb scales UP (1.10).
 *   - press:   thumb scales DOWN (0.90).
 *   - loading: thumb replaced by inline CSS spinner (no Icon dep).
 *   - focus-visible: color-tinted ring via focusRing(theme).
 *   - disabled: uniform fade via disabledStyle().
 *
 * Color:
 *   - primary (default) — neutral on/off action.
 *   - good — confirms / enables a positive feature.
 *   - bad — destructive flag ("Delete after 30 days").
 *   - alert — risky pre-release ("Beta features").
 *
 * Label / description / fullWidth match Radio + Checkbox exactly so all
 * three form-controls compose identically in a FormField or ChoiceGroup.
 *
 * NOTE: labelPosition is fixed at "label-left, switch-right". Toggle is
 * read top-to-bottom like a settings page; the switch sits at the
 * trailing edge of every row. No prop exposed.
 *
 * @format
 * @see Radio, Checkbox for sibling form-controls with the same row / description / disabled / fullWidth model.
 * @see ChoiceGroup for the multi-item compound that renders a settings- style list of Toggle items with per-item tones and optimistic Promise propagation across the group.
 */

import {forwardRef, useCallback, useEffect, useRef, useState, useId, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {keyframes, styled} from '@mui/material/styles';

import {text, type TypographyRole} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {focusRing, disabledStyle} from 'ui/theme/state';

import {Icon} from '../Icon';

export const TOGGLE_SIZES = ['s', 'm', 'l'] as const;
export type ToggleSize = (typeof TOGGLE_SIZES)[number];

export const TOGGLE_COLORS = ['primary', 'good', 'bad', 'alert'] as const;
export type ToggleColor = (typeof TOGGLE_COLORS)[number];

interface SizeGeometry {
  /** Touch-target row height — matches Radio / Checkbox at the same step. */
  row: number;
  /** Track width (≈1.625 × trackH). */
  trackW: number;
  /** Track height (== switch height). */
  trackH: number;
  /** Thumb diameter. */
  thumb: number;
  /** Padding inside the track around the thumb. */
  pad: number;
  /** Typography role for the label. */
  labelRole: TypographyRole;
  /** Symmetric vertical padding on LabelText — same trick as Radio / Checkbox. */
  labelVPad: number;
}

// Switch sizing — the TRACK is intentionally SMALLER than the row, with
// breathing room on top and bottom. This matches Radio/Checkbox where
// the glyph is also smaller than the touch-target row. The Figma
// reference (52×32) maps to size `l`.
const GEO: Record<ToggleSize, SizeGeometry> = {
  s: {row: 24, trackW: 26, trackH: 16, thumb: 12, pad: 2, labelRole: 'label.s', labelVPad: 5},
  m: {row: 32, trackW: 38, trackH: 24, thumb: 18, pad: 3, labelRole: 'label.m', labelVPad: 8},
  l: {row: 48, trackW: 52, trackH: 32, thumb: 24, pad: 4, labelRole: 'label.l', labelVPad: 14},
};

/** Threshold after which a pending async onChange shows the spinner. */
const LOADING_SPINNER_DELAY = 500;

// ── Styled primitives ──────────────────────────────────────────────────

const Root = styled('label', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: ToggleSize;
  $disabled: boolean;
  $fullWidth: boolean;
}>(({$size, $disabled, $fullWidth}) => ({
  display: 'inline-flex',
  // Top alignment — same as Radio / Checkbox. Glyph (track) is fixed
  // in vertical position; label + description stack downward without
  // shifting it.
  alignItems: 'flex-start',
  // labelPosition is fixed — label LEFT, switch RIGHT — so we always
  // reverse the natural [hidden][track][label] JSX order.
  flexDirection: 'row-reverse',
  minHeight: GEO[$size].row,
  width: $fullWidth ? '100%' : 'auto',
  cursor: 'pointer',
  userSelect: 'none',
  color: surfaceVar('onSurface'),
  ...text(GEO[$size].labelRole),
  ...($disabled && disabledStyle()),
}));

const HiddenInput = styled('input')({
  position: 'absolute',
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
  margin: 0,
});

/**
 * Track. Wraps the thumb, holds the focus ring, and provides the row
 * vertical-centering by being placed in a same-row-height anchor box.
 */
const TrackAnchor = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: ToggleSize}>(({$size}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Anchor box matches the row height so the track's vertical position
  // is identical to Radio/Checkbox glyph rows. Width matches the track.
  width: GEO[$size].trackW,
  height: GEO[$size].row,
  flexShrink: 0,
}));

const Track = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: ToggleSize;
  $color: ToggleColor;
  $checked: boolean;
  $error: boolean;
}>(({theme, $size, $color, $checked, $error}) => {
  const g = GEO[$size];
  // currentColor drives focusRing(); we set it to the color's main so the
  // ring picks the same color as the active track. Base bg is just the
  // color (ON) or surface3 (OFF) — the bad-color flash is handled by a
  // separate `&::after` overlay so it doesn't fight the bg transition.
  return {
    position: 'relative',
    display: 'inline-block',
    width: g.trackW,
    height: g.trackH,
    borderRadius: g.trackH / 2,
    flexShrink: 0,
    color: theme.palette[$color].main,
    backgroundColor: $checked ? theme.palette[$color].main : surfaceVar('surface3'),
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shorter,
    }),
    // Bad-overlay sits ABOVE base bg but BELOW the thumb (`zIndex: 1`).
    // Default opacity 0; while `$error` is true the keyframes ramp it
    // in fast (20ms attack) and bleed it out slowly (150ms decay).
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      backgroundColor: theme.palette.bad.main,
      opacity: 0,
      pointerEvents: 'none',
      ...($error && {animation: `${errorPulseKf} 170ms linear`}),
    },
    ...focusRing(theme, {parentFocusVisible: true}),
  };
});

const Thumb = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: ToggleSize;
  $color: ToggleColor;
  $checked: boolean;
}>(({theme, $size, $color, $checked}) => {
  const g = GEO[$size];
  const travel = g.trackW - g.thumb - g.pad * 2;
  // Thumb takes the high-contrast pair of whatever sits behind it:
  //   OFF: track surface3 → thumb onSurface (light dot on gray track).
  //   ON:  track color.main → thumb color.contrastText (white on the color).
  // `background.default` blends with the page and looked muddy.
  //
  // Context-flip for OFF: `onSurface` is a TEXT token — it inverts to dark
  // in light context, which gives a black thumb on a light surface (looks
  // wrong, reads as a "knob in the dark" UI element). In light context we
  // swap to `inverseOnSurface` so the thumb stays the same visual weight
  // (light dot) as in dark context. ON state uses `color.contrastText`
  // which is already invariant across contexts.
  const thumbBg = $checked ? theme.palette[$color].contrastText : surfaceVar('onSurface');
  return {
    position: 'absolute',
    top: g.pad,
    left: g.pad,
    width: g.thumb,
    height: g.thumb,
    borderRadius: '50%',
    // Thumb sits ABOVE the bad-flash overlay (`Track::after`).
    zIndex: 1,
    backgroundColor: thumbBg,
    // Override OFF-state thumb in any subtree wrapped by SurfaceContext-
    // Provider with `value="light"` (it sets data-color-scheme="light" on
    // its wrapper div, so the selector matches via the cascade).
    ...(!$checked && {
      '[data-color-scheme="light"] &': {
        backgroundColor: surfaceVar('inverseOnSurface'),
      },
    }),
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Icon inside the thumb inverts to whatever is behind the thumb.
    color: $checked ? theme.palette[$color].main : surfaceVar('surface3'),
    // X = slide position (off → 0, on → travel)
    // SCALE = 1.0 default. Hover & press alter only the scale axis so
    // the slide animation stays straight.
    transform: `translateX(${$checked ? travel : 0}px) scale(1)`,
    transition: theme.transitions.create(['transform', 'background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    // Hover: grow. Press: shrink. Both keyed off the parent Root so the
    // whole row reacts (label-hover too).
    'label:hover &': {
      transform: `translateX(${$checked ? travel : 0}px) scale(1.10)`,
    },
    'label:active &': {
      transform: `translateX(${$checked ? travel : 0}px) scale(0.90)`,
    },
  };
});

const spin = keyframes({
  to: {transform: 'rotate(360deg)'},
});

// Error pulse for the track. Overlay opacity ramps from 0 to 1 in the
// first ~20 ms (fast "no" attack) then bleeds back to 0 over ~150 ms
// (slow decay). Total animation 170 ms.
// Used when the optimistic onChange promise rejects.
const errorPulseKf = keyframes({
  '0%': {opacity: 0},
  '12%': {opacity: 1},
  '100%': {opacity: 0},
});

const ThumbSpinner = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: ToggleSize; $color: ToggleColor}>(({theme, $size, $color}) => {
  const g = GEO[$size];
  // Spinner sits flush inside the thumb. Stroke uses the color's main.
  const stroke = 2;
  const ring = g.thumb - stroke * 2 - 2; // 2px breathing room
  return {
    display: 'inline-block',
    width: ring,
    height: ring,
    borderRadius: '50%',
    border: `${stroke}px solid ${theme.palette[$color].main}`,
    borderTopColor: 'transparent',
    animation: `${spin} 0.8s linear infinite`,
  };
});

const LabelText = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: ToggleSize}>(({theme, $size}) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flex: '1 1 auto',
  minWidth: 0,
  paddingTop: GEO[$size].labelVPad,
  paddingBottom: GEO[$size].labelVPad,
  // Gap to the trailing track. Tighter than gap between adornments
  // because the track is a visually anchored block.
  paddingRight: theme.spacing(3),
  gap: theme.spacing(1),
}));

const DescriptionText = styled(Box)(({}) => ({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
  whiteSpace: 'normal',
}));

// ── Component ─────────────────────────────────────────────────────────

export interface ToggleThumbIcons {
  /** Material Symbols name shown inside the thumb when the toggle is ON. */
  on?: string;
  /** Material Symbols name shown inside the thumb when the toggle is OFF. */
  off?: string;
}

export interface ToggleProps {
  // ── Required state ────────────────────────────────────────────────
  /** Controlled state. */
  checked: boolean;
  /**
   * Fires with the next boolean. Returning a `Promise<void>` lets the
   * Toggle show a spinner in the thumb after `500 ms` and ignores
   * further clicks until the promise settles.
   */
  onChange: (next: boolean) => void | Promise<void>;
  /**
   * Label rendered to the LEFT of the switch. REQUIRED — a bare switch
   * carries no meaning. Use `ariaLabel` separately if the visible label
   * is a React node and you need a clean string for AT.
   */
  label: ReactNode;

  // ── Visual ────────────────────────────────────────────────────────
  /** Default `'m'`. */
  size?: ToggleSize;
  /** Default `'primary'`. Choose `good` / `bad` / `alert` for semantic toggles. */
  color?: ToggleColor;

  // ── Content ───────────────────────────────────────────────────────
  /** Optional secondary muted line below the label. */
  description?: ReactNode;
  /**
   * Optional Material Symbols glyphs rendered inside the thumb. Sized
   * to the thumb diameter; the thumb clips overflow.
   */
  thumbIcon?: ToggleThumbIcons;

  // ── Behaviour ─────────────────────────────────────────────────────
  /** Default `false`. */
  disabled?: boolean;

  // ── Layout ────────────────────────────────────────────────────────
  /** Default `true` — fills the parent column so the entire row is clickable. */
  fullWidth?: boolean;

  /**
   * Screen-reader-only label for the switch. Required when the visible
   * `label` is a React node (icon, badge, multi-element) and you need
   * a clean string for assistive tech.
   */
  ariaLabel?: string;
  /**
   * Pass-through className applied to the outer `<label>` element.
   * Use only for one-off positioning overrides.
   */
  className?: string;
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  {
    checked,
    onChange,
    label,
    description,
    size = 'm',
    color = 'primary',
    disabled = false,
    fullWidth = true,
    thumbIcon,
    ariaLabel,
    className,
  },
  ref,
) {
  const inputId = useId();

  // Optimistic pattern:
  //   1. User clicks → we IMMEDIATELY render the next state (`optimistic`).
  //   2. If onChange returns a Promise, we keep optimistic until either
  //      the consumer's `checked` prop catches up (success path) or
  //      the promise rejects (we revert + bad-flash).
  //   3. If the promise hasn't settled in 500 ms, we show a spinner in
  //      the thumb so the user knows something is in flight.
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorPulse, setErrorPulse] = useState(false);

  // Refs that survive unmount + stale closures.
  const pendingRef = useRef(false);
  const mountedRef = useRef(true);
  const spinnerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (spinnerTimerRef.current != null) clearTimeout(spinnerTimerRef.current);
      if (pulseTimerRef.current != null) clearTimeout(pulseTimerRef.current);
    };
  }, []);

  // When the consumer's `checked` finally catches up to whatever we
  // optimistically showed, drop the optimistic override. Also clears on
  // any external `checked` change that contradicts the optimistic state
  // (defensive — keeps display in sync with truth).
  useEffect(() => {
    setOptimistic(null);
  }, [checked]);

  const handleChange = useCallback(
    (next: boolean) => {
      if (disabled || pendingRef.current) return;
      const result = onChange(next);
      if (result && typeof (result as Promise<void>).then === 'function') {
        // Optimistic flip — render the new state immediately.
        setOptimistic(next);
        pendingRef.current = true;
        spinnerTimerRef.current = setTimeout(() => {
          if (pendingRef.current && mountedRef.current) setLoading(true);
        }, LOADING_SPINNER_DELAY);
        (result as Promise<void>)
          .then(() => {
            // Success — we keep showing `next` via the optimistic state
            // until the consumer's `checked` prop catches up. The useEffect
            // above will then clear `optimistic`.
          })
          .catch(() => {
            if (!mountedRef.current) return;
            // Sequence the two visual events to avoid overlap:
            //   1. While the bad-overlay flashes (170ms), KEEP the
            //      optimistic state so the track bg + thumb position
            //      stay where the user clicked. The user perceives only
            //      one event — a single red beat — without a competing
            //      bg transition.
            //   2. After the flash settles, drop the optimistic state.
            //      Only then does the thumb slide back + bg transition
            //      to its real value, as a separate, clean motion.
            setErrorPulse(true);
            if (pulseTimerRef.current != null) clearTimeout(pulseTimerRef.current);
            pulseTimerRef.current = setTimeout(() => {
              if (!mountedRef.current) return;
              setErrorPulse(false);
              setOptimistic(null);
            }, 170);
          })
          .finally(() => {
            pendingRef.current = false;
            if (spinnerTimerRef.current != null) clearTimeout(spinnerTimerRef.current);
            spinnerTimerRef.current = null;
            if (mountedRef.current) setLoading(false);
          });
      }
    },
    [disabled, onChange],
  );

  // What the visual primitives should reflect: optimistic if we have one,
  // otherwise the controlled `checked` prop.
  const displayChecked = optimistic ?? checked;
  const glyphPx = GEO[size].thumb;
  const thumbIconName = displayChecked ? thumbIcon?.on : thumbIcon?.off;
  const effectivelyDisabled = disabled || loading;

  return (
    <Root
      htmlFor={inputId}
      $size={size}
      $disabled={effectivelyDisabled}
      $fullWidth={fullWidth}
      className={className}
    >
      <HiddenInput
        ref={ref}
        type='checkbox'
        id={inputId}
        checked={displayChecked}
        disabled={effectivelyDisabled}
        aria-label={ariaLabel}
        role='switch'
        aria-checked={displayChecked}
        aria-busy={loading || undefined}
        onChange={e => handleChange(e.target.checked)}
      />
      <TrackAnchor $size={size}>
        <Track
          $size={size}
          $color={color}
          $checked={displayChecked}
          $error={errorPulse}
        >
          <Thumb $size={size} $color={color} $checked={displayChecked}>
            {loading ? (
              <ThumbSpinner $size={size} $color={color} />
            ) : thumbIconName ? (
              <Icon name={thumbIconName} size={glyphPx} color='inherit' />
            ) : null}
          </Thumb>
        </Track>
      </TrackAnchor>
      <LabelText $size={size}>
        <Box component='span'>{label}</Box>
        {description != null && <DescriptionText>{description}</DescriptionText>}
      </LabelText>
    </Root>
  );
});
